import React, { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FORM_COPY,
  FORM_PROPERTIES,
  HUBSPOT_FORM_IDS,
  getCountrySelectOptions,
  getIndustrySelectOptions,
  getHubspotLanguage
} from './formConfig';
import { PhoneField, SelectField, TextareaField, TextField } from './FormField';
import { getHubspotCookie, getHubspotSubmissionUrl, getSubmissionErrors } from './submission';
import type { FieldErrors, HubspotSubmissionResponse, SubmissionState } from './types';
import styles from './ContactUsForm.module.scss';

const ContactForm = () => {
  const { i18n } = useTranslation();
  const language = getHubspotLanguage(i18n.language);
  const formId = HUBSPOT_FORM_IDS[language];
  const copy = FORM_COPY[language];
  const countryOptions = getCountrySelectOptions(language);
  const industryOptions = getIndustrySelectOptions(language);
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const submissionInProgress = useRef(false);

  const handleFieldChange = (event: FormEvent<HTMLFormElement>) => {
    const fieldName = (event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).name;
    const property = FORM_PROPERTIES.find((item) => item.reference === fieldName);
    if (!property || !fieldErrors[property.name]) return;

    setFieldErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[property.name];
      return nextErrors;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionInProgress.current || submissionState === 'submitting' || !event.currentTarget.reportValidity()) {
      return;
    }

    const form = event.currentTarget;
    const data = new FormData(form);
    const submittedFields = FORM_PROPERTIES.flatMap((property) => {
      const value = data.get(property.reference);
      if (typeof value !== 'string' || value.trim() === '') return [];

      const element = form.elements.namedItem(property.reference);
      const dialPrefix = element instanceof HTMLInputElement ? element.dataset.dialPrefix : undefined;
      if (property.name === 'phone' && value.trim() === dialPrefix) return [];

      return [{ objectTypeId: property.objectTypeId, name: property.name, value: value.trim() }];
    });

    setSubmissionState('submitting');
    setSubmissionMessage('');
    setFieldErrors({});
    submissionInProgress.current = true;

    try {
      const response = await fetch(getHubspotSubmissionUrl(formId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submittedAt: Date.now().toString(),
          fields: submittedFields,
          context: {
            hutk: getHubspotCookie(),
            pageName: document.title,
            pageUri: window.location.href
          }
        })
      });

      if (!response.ok) {
        const submissionErrors = await getSubmissionErrors(response);
        setFieldErrors(submissionErrors.fieldErrors);
        setSubmissionMessage(submissionErrors.message || copy.submissionError);
        setSubmissionState('error');
        return;
      }

      const result = (await response.json()) as HubspotSubmissionResponse;
      if (result.redirectUri) {
        window.location.assign(result.redirectUri);
        return;
      }

      setSubmissionMessage(result.inlineMessage?.replace(/<[^>]*>/g, '').trim() || copy.success);
      setSubmissionState('success');
    } catch {
      setSubmissionMessage(copy.submissionError);
      setSubmissionState('error');
    } finally {
      submissionInProgress.current = false;
    }
  };

  if (submissionState === 'success') {
    return (
      <div className={styles.formShell}>
        <div className={styles.status} role='status'>
          {submissionMessage}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formShell}>
      <form
        className={styles.form}
        data-testid='hubspot-contact-form'
        onChange={handleFieldChange}
        onSubmit={handleSubmit}
      >
        <div className={styles.row}>
          <TextField
            error={fieldErrors.firstname}
            formId={formId}
            label={copy.fields.firstname}
            name='0-1/firstname'
            required
          />
          <TextField
            error={fieldErrors.lastname}
            formId={formId}
            label={copy.fields.lastname}
            name='0-1/lastname'
            required
          />
        </div>
        <div className={styles.row}>
          <TextField
            error={fieldErrors.email}
            formId={formId}
            label={copy.fields.email}
            name='0-1/email'
            required
            type='email'
          />
          <PhoneField
            countryLabel={copy.phoneCountry}
            error={fieldErrors.phone}
            formId={formId}
            label={copy.fields.phone}
            language={language}
            name='0-1/phone'
          />
        </div>
        <div className={styles.row}>
          <TextField error={fieldErrors.name} formId={formId} label={copy.fields.name} name='0-2/name' required />
          <SelectField
            error={fieldErrors.country_select_eezi}
            formId={formId}
            label={copy.fields.country_select_eezi}
            lazyOptions
            name='0-1/country_select_eezi'
            options={countryOptions}
            required
          />
        </div>
        <div className={styles.row}>
          <TextField
            error={fieldErrors.jobtitle}
            formId={formId}
            label={copy.fields.jobtitle}
            name='0-1/jobtitle'
            required
          />
          <SelectField
            error={fieldErrors.industry_select_eezi}
            formId={formId}
            label={copy.fields.industry_select_eezi}
            name='0-1/industry_select_eezi'
            options={industryOptions}
          />
        </div>
        <div className={styles.row + ' ' + styles.fullRow}>
          <TextareaField
            error={fieldErrors.message}
            formId={formId}
            label={copy.fields.message}
            name='0-1/message'
            required
          />
        </div>
        <div className={styles.row + ' ' + styles.fullRow}>
          <div className={styles.navigation}>
            <button className={styles.submit} type='submit' disabled={submissionState === 'submitting'}>
              {submissionState === 'submitting' ? copy.submitting : copy.submit}
            </button>
          </div>
        </div>
        {submissionState === 'error' && (
          <div className={styles.errorSummary} role='alert'>
            {submissionMessage}
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
